import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { EmailOtpType } from '@supabase/supabase-js';
import { LinkIcon, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { APP_CONFIG } from '@/config/app';
import { supabase } from '@/integrations/supabase/client';
import { isCustomerUser } from '@/integrations/supabase/session';
import { useAuth } from '@/hooks/useAuth';
import { portalDb } from '../hooks/portalClient';
import { rememberPortalSlug } from '../returnTo';
import { PortalLoading } from '../components/PortalLoading';

type State = 'verifying' | 'confirm' | 'error';

/**
 * Landing page for the emailed magic link:
 * `/portal/:slug/auth?token_hash=…&type=magiclink` (ResetPassword precedent).
 * Exchanges the hash for a session, strips it from the URL and goes home.
 */
export default function PortalAuthCallback() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const tokenHash = params.get('token_hash');
  const type = (params.get('type') || 'magiclink') as EmailOtpType;

  const [state, setState] = useState<State>('verifying');
  // StrictMode runs effects twice; a token hash can only be exchanged once.
  const started = useRef(false);

  const exchange = useCallback(async () => {
    if (!tokenHash) {
      setState('error');
      return;
    }
    setState('verifying');
    const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
    if (error) {
      setState('error');
      return;
    }
    window.history.replaceState(null, '', window.location.pathname);
    // PostgREST builders only run when awaited/then'd; best effort, never blocks.
    void portalDb.rpc('portal_touch_login').then(() => undefined, () => undefined);
    rememberPortalSlug(slug);
    navigate(`/portal/${slug}/home`, { replace: true });
  }, [tokenHash, type, slug, navigate]);

  useEffect(() => {
    if (authLoading || started.current) return;
    if (!tokenHash) {
      started.current = true;
      setState('error');
      return;
    }
    if (user && !isCustomerUser(user)) {
      // A staff session is in this browser: never swap it silently.
      setState('confirm');
      return;
    }
    started.current = true;
    void exchange();
  }, [authLoading, user, tokenHash, exchange]);

  const continueAsCustomer = async () => {
    if (started.current) return;
    started.current = true;
    await supabase.auth.signOut();
    await exchange();
  };

  if (state === 'confirm') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <ShieldAlert className="mx-auto mb-4 h-12 w-12 text-warning" />
            <h1 className="mb-2 text-lg font-semibold">Switch to the customer portal?</h1>
            <p className="text-sm text-muted-foreground">
              You're signed in to {APP_CONFIG.name} with a staff account. Opening this link will sign that account out
              of this browser and sign you in as the customer.
            </p>
            <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
              <Button asChild variant="outline">
                <Link to="/dashboard">Keep my staff session</Link>
              </Button>
              <Button onClick={() => void continueAsCustomer()}>Continue as customer</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <LinkIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h1 className="mb-2 text-lg font-semibold">This link has expired or was already used</h1>
            <p className="text-sm text-muted-foreground">
              Sign-in links work once and expire after a short while. Request a new one and we'll email it straight away.
            </p>
            <Button asChild className="mt-6">
              <Link to={`/portal/${slug}`}>Request a new link</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <PortalLoading fullScreen label="Signing you in…" />;
}
